import React from "react";
import {Flex, SwitchField} from "@aws-amplify/ui-react";
import EiaButton from "../components/EiaButton";
import {d4GetMyAuctions, d4SearchAuctions} from "../commons/api/D4Api";
import {contains, findByKeyValue} from "../commons/Utils/ArrayUtils";
import {BNET_SEARCH_TABLE_HEADERS_NO_STATS, D4_SEARCH_TABLE_HEADERS, getAdditionalHeaders} from "../commons/TableHeaders";
import ShowDetailsButton from "../components/ShowDetailsButton";
import {auctionToTableElement, convertToD4ItemForRequest} from "../commons/Converter";
import Utils from "../commons/Utils/Utils";
import EiaAuctionTableWithButtons from "../components/tables/EiaAuctionTableWithButtons";
import FiltersDrawer from "../components/drawer/FiltersDrawer";
import {useCookies} from "react-cookie";
import {ITEM_SUB_TYPES_WITHOUT_STATS, MODES, PLATFORMS, REALM, REGIONS} from "../commons/enums/D4Enums";
import EiaAuctionGridWithButtons from "../components/grids/EiaAuctionGridWithButtons";
import EiaInfoText from "../components/EiaInfoText";
import BidDialog from "../components/dialogs/BidDialog";
import * as ApiUtils from "../commons/api/ApiUtils";
import {GAMES} from "../commons/enums/EiaEnums";

const tableInitialState = {
    sorting: {
        sortModel: [{ field: 'creationDate', sort: 'desc' }],
    },
}

const bestPricesTableInitialState = {
    sorting: {
        sortModel: [{ field: 'itemName', sort: 'asc' }],
    },
}

const isSmallScreen = Utils.isSmallScreen();
const buttonsDirection = isSmallScreen ? "row" : "column"

const SearchBasePage = function (props) {
    const {user, optionalLeftElements,
        currency, setCurrency,
        baseItem, setBaseItem,
        seller, setSeller,
        inheritedBaseStats, setInheritedBaseStats,
        inheritedAspects, setInheritedAspects,
        inheritedUniqueAffixes, setInheritedUniqueAffixes,
        inheritedSetAffixes, setInheritedSetAffixes,
        inheritedMythicAffixes, setInheritedMythicAffixes,
        inheritedAffixStats, setInheritedAffixStats,
        initialBaseStats={},
        initialAffixStats=[],
        initialAspects=[],
        initialUniqueAffixes=[],
        initialSetAffixes=[],
        initialMythicAffixes=[],
        onlyBestPricePerItemName,
        activateFiltersDrawer,
        allowBids=true,
        overItemsRightElements
    } = props;

    const [cookies, setCookies, removeCookies] = useCookies(['game', 'mode', 'realm', 'region', 'platform']);

    const cookiesWrapper = {
        game: cookies.game ? cookies.game : GAMES[0],
        mode: cookies.mode ? cookies.mode : MODES[0],
        realm: cookies.realm ? cookies.realm : REALM[0],
        region: cookies.region ? cookies.region : REGIONS[0],
        platform: cookies.platform ? cookies.platform : PLATFORMS[0]
    }

    const [tableView, setTableView] = React.useState(false);
    const [hideOwnItems, setHideOwnItems] = React.useState(false);

    //we want to support inherited state from props and also an internal state if inherited is not there
    const [internalBaseStats, setInternalBaseStats] = React.useState(initialBaseStats);
    const baseStats = inheritedBaseStats ? inheritedBaseStats : internalBaseStats;
    const setBaseStats = setInheritedBaseStats ? setInheritedBaseStats : setInternalBaseStats;

    //we want to support inherited state from props and also an internal state if inherited is not there
    const [internalAffixStats, setInternalAffixStats] = React.useState(initialAffixStats);
    const affixStats = inheritedAffixStats ? inheritedAffixStats : internalAffixStats;
    const setAffixStats = setInheritedAffixStats ? setInheritedAffixStats : setInternalAffixStats;

    //we want to support inherited state from props and also an internal state if inherited is not there
    const [internalAspects, setInternalAspects] = React.useState(initialAspects);
    const aspects = inheritedAspects ? inheritedAspects : internalAspects;
    const setAspects = setInheritedAspects ? setInheritedAspects : setInternalAspects;

    //we want to support inherited state from props and also an internal state if inherited is not there
    const [internalUniqueAffixes, setInternalUniqueAffixes] = React.useState(initialUniqueAffixes);
    const uniqueAffixes = inheritedUniqueAffixes ? inheritedUniqueAffixes : internalUniqueAffixes;
    const setUniqueAffixes = setInheritedUniqueAffixes ? setInheritedUniqueAffixes : setInternalUniqueAffixes;

    //we want to support inherited state from props and also an internal state if inherited is not there
    const [internalSetAffixes, setInternalSetAffixes] = React.useState(initialSetAffixes);
    const setAffixes = inheritedSetAffixes ? inheritedSetAffixes : internalSetAffixes;
    const setSetAffixes = setInheritedSetAffixes ? setInheritedSetAffixes : setInternalSetAffixes;

    //we want to support inherited state from props and also an internal state if inherited is not there
    const [internalMythicAffixes, setInternalMythicAffixes] = React.useState(initialMythicAffixes);
    const mythicAffixes = inheritedMythicAffixes ? inheritedMythicAffixes : internalMythicAffixes;
    const setMythicAffixes = setInheritedMythicAffixes ? setInheritedMythicAffixes : setInternalMythicAffixes;

    const [alert, setAlert] = React.useState(null)
    const [tableData, setTableData] = React.useState([])
    const [isLoading, setIsLoading] = React.useState(false)
    const [selectedAuctionId, setSelectedAuctionId] = React.useState([])

    const filteredTableData = hideOwnItems && user !== undefined ? tableData.filter(element => element.seller != user.username) : tableData;

    const [openBidDialog, setOpenBidDialog] = React.useState(false);

    const [tableHeader, setTableHeader] = React.useState(D4_SEARCH_TABLE_HEADERS);

    const selectedAuction = selectedAuctionId.length === 0 ? null : findByKeyValue(filteredTableData, "id", selectedAuctionId);

    const [initSearchDone, setInitSearchDone] = React.useState(false);

    React.useEffect(() => {
        if(currency !== undefined && currency.name !== undefined && !initSearchDone) {
            searchAuctions();
            setInitSearchDone(true);
        }
    }, [currency]);

    const searchAuctions = function (keepAlert) {
        const alertFunction = keepAlert ? Utils.doNothing : setAlert;

        setSelectedAuctionId([])
        setIsLoading(true)
        alertFunction(null);

        const request = ApiUtils.getGameRequest(cookiesWrapper);
        const itemForRequest = convertToD4ItemForRequest(baseItem, baseStats, null, affixStats, aspects, uniqueAffixes, setAffixes, mythicAffixes, request.game);

        request.currency = currency ? currency.name : "Any";
        request.seller = seller ? seller.username : "";
        request.item = itemForRequest;
        request.status = "ACTIVE";
        request.onlyBestPricePerItemName = onlyBestPricePerItemName;

        d4SearchAuctions(request, function (response) {
            if(response.auctions) {
                if(!onlyBestPricePerItemName && response.auctions.length >= 1000) {
                    alertFunction(["warning", "Your search results reached the maximum limit. Please use more search"
                    + " filters."]);
                }

                let tempTableData = response.auctions.map(auction => {
                    return auctionToTableElement(auction);
                })

                setTableData(tempTableData)
            } else {
                setTableData([])
            }

            if(contains(ITEM_SUB_TYPES_WITHOUT_STATS, baseItem.itemSubType)) {
                setTableHeader(BNET_SEARCH_TABLE_HEADERS_NO_STATS);
            } else {
                setTableHeader(D4_SEARCH_TABLE_HEADERS.concat(getAdditionalHeaders(baseItem.itemType, baseItem.itemSubType, affixStats)));
            }

            setIsLoading(false)
        }, function (error) {
            alertFunction(["error", error.message]);
            setTableData([])
            setIsLoading(false)
        })
    }

    const showAllFromSeller = function () {
        const seller = selectedAuction.seller;
        setSelectedAuctionId([])
        setIsLoading(true)

        const request = ApiUtils.getGameRequest(cookiesWrapper);
        request.optionalSeller = seller;

        d4GetMyAuctions(request, function (response) {
            if(response.auctions) {
                let tempTableData = response.auctions.map(auction => {
                    return auctionToTableElement(auction);
                })

                setTableData(tempTableData)
            } else {
                setTableData([])
            }

            setTableHeader(D4_SEARCH_TABLE_HEADERS);

            setIsLoading(false)

        }, function (error) {
            setAlert(["error", error.message]);
            setTableData([])
            setIsLoading(false)
        })
    }

    const handleClickAuctionInNonTableView=function(auction) {
        setSelectedAuctionId(auction.id);
        setOpenBidDialog(true);
    }

    const buttons = <Flex direction={"column"}>
        <SwitchField
            isDisabled={isLoading}
            label="Table View"
            labelPosition="end"
            isChecked={tableView}
            onChange={(e) => {
                setTableView(e.target.checked);
            }}
        />
        <SwitchField
            isDisabled={isLoading}
            label="Hide own items"
            labelPosition="end"
            isChecked={hideOwnItems}
            onChange={(e) => {
                if(e.target.checked === false) {
                    setSelectedAuctionId([])
                }
                setHideOwnItems(e.target.checked);
            }}
        />
        {optionalLeftElements}
        <Flex direction={buttonsDirection} justifyContent="space-between">
            <EiaButton onClick={searchAuctions} disabled={isLoading} isFullWidth tooltip={"Reloads the item data"}>Reload</EiaButton>
            {activateFiltersDrawer ? <FiltersDrawer
                {... {currency, setCurrency, baseItem, setBaseItem, seller, setSeller, baseStats, setBaseStats, affixStats, setAffixStats, setAlert, searchAuctions,
                    isLoading, aspects, setAspects, uniqueAffixes, setUniqueAffixes, setAffixes, setSetAffixes, mythicAffixes, setMythicAffixes}}
            /> : null}
        </Flex>
        { !allowBids || tableView ? null : <EiaInfoText value={"Click an item to bid"} /> }
        {tableView ?
            <Flex direction={buttonsDirection} justifyContent="space-between">
                <ShowDetailsButton isFullWidth disabled={isLoading || selectedAuctionId.length === 0} title={"Details"}
                                   details={findByKeyValue(filteredTableData, "id", selectedAuctionId)}/>
                { allowBids && !onlyBestPricePerItemName ? <EiaButton onClick={showAllFromSeller} disabled={isLoading || selectedAuctionId.length === 0}
                    isFullWidth tooltip={"Shows all listings from the seller of the selected listing"}>Show all from seller</EiaButton> : null}
                { allowBids ?
                <EiaButton colorTheme={"success"} onClick={() => setOpenBidDialog(true)} disabled={isLoading || selectedAuctionId.length === 0}
                           isFullWidth tooltip={"Bid a currency and price of your choice"}>Bid ...</EiaButton> : null }
            </Flex> : null}
    </Flex>

    const isMoreDataInBackend = tableData !== undefined && tableData.length === 100;

    return (<div>
        {allowBids && selectedAuction ?
            <BidDialog
                open={openBidDialog}
                setOpen={setOpenBidDialog}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                setAlert={setAlert}
                selectedAuction={selectedAuction}
                selectedAuctionId={selectedAuctionId}
                searchAuctions={searchAuctions}
            /> : null
        }
        {
            tableView ? <EiaAuctionTableWithButtons
                buttons={buttons}
                alert={alert}
                selectedAuction={selectedAuction}
                selectedAuctionId={selectedAuctionId}
                setSelectedAuctionId={setSelectedAuctionId}
                isLoading={isLoading}
                tableData={filteredTableData}
                isMoreDataInBackend={isMoreDataInBackend}
                tableHeader={tableHeader}
                tableInitialState={onlyBestPricePerItemName ? bestPricesTableInitialState : tableInitialState}
                overItemsRightElements={overItemsRightElements}
            /> : <EiaAuctionGridWithButtons
                buttons={buttons}
                alert={alert}
                isLoading={isLoading}
                auctions={filteredTableData}
                isMoreDataInBackend={isMoreDataInBackend}
                onClickAuction={allowBids ? handleClickAuctionInNonTableView : null}
                supportSorting={!onlyBestPricePerItemName}
                defaultSortIndex={0}
                overItemsRightElements={overItemsRightElements}
            />
        }
    </div>)
}
export default SearchBasePage;